// Hospice Academy Course Data - ELNEC-aligned curriculum with CEU credits + Discipline-specific tracks
import {
  UserCog, Bandage, Home, Users, Cross, Heart, 
  BookOpen, Clock, MessageSquare, Shield, ScrollText, Stethoscope
} from 'lucide-react';

export const ELNEC_MODULES = {
  // RN Case Manager Track
  rncm_track: {
    title: "RN Case Manager (RNCM)",
    description: "Comprehensive case management certification for hospice RNs",
    icon: UserCog,
    color: "bg-rose-600",
    totalCEUs: 12.0,
    discipline: "RN",
    courses: [
      {
        id: "rncm-1",
        title: "Hospice Eligibility & LCDs",
        description: "Local Coverage Determinations for common hospice diagnoses, terminal prognosis indicators",
        duration: "45m",
        level: "RNCM",
        youtubeId: "CXuJc35-gf0",
        instructor: "Clinical Education",
        ceuCredits: 1.0,
        objectives: [
          "Interpret LCDs for common diagnoses",
          "Identify terminal prognosis indicators",
          "Document eligibility criteria",
          "Apply disease-specific guidelines"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-2",
        title: "Initial POC & Comprehensive Assessments",
        description: "Required elements, medication alignment, visit frequencies. Domains: physical, psychosocial, spiritual, bereavement",
        duration: "1h",
        level: "RNCM",
        youtubeId: "82QsMmA5cKs",
        startTime: 1200,
        instructor: "Clinical Education",
        ceuCredits: 1.0,
        objectives: [
          "Complete comprehensive assessments",
          "Align medications with terminal diagnosis",
          "Set appropriate visit frequencies",
          "Document all four domains"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-3",
        title: "Care Planning & Goal Setting",
        description: "Personalized goals, unresolved problems, measurable outcomes using HOPE tools",
        duration: "45m",
        level: "RNCM",
        youtubeId: "URjkGCvlWKg",
        startTime: 1800,
        instructor: "Quality Team",
        ceuCredits: 1.0,
        objectives: [
          "Write personalized patient goals",
          "Address unresolved problems",
          "Create measurable outcomes",
          "Apply HOPE assessment tools"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-4",
        title: "Symptom Management & HOPE Tools",
        description: "Escalation of care, documentation of response, symptom tracking",
        duration: "1h",
        level: "RNCM",
        youtubeId: "uFuyKotwKTA",
        instructor: "Clinical Education",
        ceuCredits: 1.0,
        objectives: [
          "Use HOPE symptom tools effectively",
          "Document symptom response",
          "Escalate care appropriately",
          "Track symptom trends"
        ],
        required: true,
        discipline: "RN",
        cahpsRelated: true
      },
      {
        id: "rncm-5",
        title: "Psychosocial & Spiritual Care",
        description: "Psychological symptoms, collaboration, documentation of symptoms and POC",
        duration: "45m",
        level: "RNCM",
        youtubeId: "URjkGCvlWKg",
        startTime: 2400,
        instructor: "Social Work/Chaplaincy",
        ceuCredits: 1.0,
        objectives: [
          "Assess psychological symptoms",
          "Collaborate with SW and chaplain",
          "Document spiritual needs",
          "Update POC accordingly"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-6",
        title: "Care of Family & Caregiver",
        description: "Psychosocial, spiritual, cultural care. Grief, loss, complex relationships, bereavement",
        duration: "1h",
        level: "RNCM",
        youtubeId: "82QsMmA5cKs",
        startTime: 3600,
        instructor: "IDT Education",
        ceuCredits: 1.0,
        objectives: [
          "Support family caregivers",
          "Address complex relationships",
          "Initiate bereavement support",
          "Respect cultural differences"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-7",
        title: "Hospitalizations, Revocations & CAP",
        description: "Early warning signs, intervention documentation, CAP tracking",
        duration: "45m",
        level: "RNCM",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 900,
        instructor: "Compliance Team",
        ceuCredits: 1.0,
        objectives: [
          "Identify hospitalization warning signs",
          "Document interventions timely",
          "Track CAP metrics",
          "Prevent avoidable revocations"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-8",
        title: "Visit Frequency & Documentation",
        description: "HOPE tools, timely symptom management, documentation protocols, audit readiness",
        duration: "45m",
        level: "RNCM",
        youtubeId: "82QsMmA5cKs",
        startTime: 4800,
        instructor: "Quality Assurance",
        ceuCredits: 1.0,
        objectives: [
          "Set appropriate visit frequency",
          "Document timely interventions",
          "Maintain audit-ready records",
          "Use HOPE visit tools"
        ],
        required: true,
        discipline: "RN",
        cahpsRelated: true
      },
      {
        id: "rncm-9",
        title: "EOL Pharmacology",
        description: "Medications for symptom management, side effects, deprescribing, reconciliation post-hospitalization",
        duration: "1h",
        level: "RNCM",
        youtubeId: "uFuyKotwKTA",
        startTime: 1800,
        instructor: "Pharmacy Liaison",
        ceuCredits: 1.5,
        objectives: [
          "Manage EOL symptom medications",
          "Identify common side effects",
          "Apply deprescribing principles",
          "Reconcile meds post-hospital"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-10",
        title: "Last 7 Days of Life",
        description: "Recognizing active dying, death visits, anticipatory medication orders",
        duration: "45m",
        level: "RNCM",
        youtubeId: "b8Eqw2RBRiQ",
        instructor: "Clinical Education",
        ceuCredits: 1.0,
        objectives: [
          "Recognize active dying signs",
          "Conduct death visits properly",
          "Order anticipatory medications",
          "Support family at death"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "rncm-11",
        title: "Compliance & Quality Roundtable",
        description: "Case reviews, quality metrics, regulatory updates, best practices",
        duration: "30m",
        level: "RNCM",
        youtubeId: "CXuJc35-gf0",
        startTime: 2400,
        instructor: "Compliance Officer",
        ceuCredits: 0.5,
        objectives: [
          "Review compliance requirements",
          "Discuss quality metrics",
          "Apply regulatory updates",
          "Share best practices"
        ],
        required: true,
        discipline: "RN"
      }
    ]
  },
  // Wound Care Track
  wound_care: {
    title: "Wound Care & Skin Integrity",
    description: "Pressure injuries, staging, documentation, prevention protocols",
    icon: Bandage,
    color: "bg-amber-600",
    totalCEUs: 3.0,
    courses: [
      {
        id: "wound-1",
        title: "Hospice Wound Care Fundamentals",
        description: "Goals of wound care in hospice: comfort and containment vs. healing",
        duration: "30m",
        level: "All Disciplines",
        youtubeId: "mFWoFn88Y_A",
        instructor: "Wound Care Specialist",
        ceuCredits: 0.5,
        objectives: [
          "Understand comfort-focused wound care",
          "Differentiate healing vs. palliative goals",
          "Assess wound characteristics",
          "Document wound status"
        ],
        required: true
      },
      {
        id: "wound-2",
        title: "Pressure Injury Staging",
        description: "Staging pressure ulcers, unstageable wounds, deep tissue injury",
        duration: "45m",
        level: "RN/LPN",
        youtubeId: "VOthk_1VNyU",
        instructor: "Wound Care Specialist",
        ceuCredits: 1.0,
        objectives: [
          "Stage pressure injuries I-IV",
          "Identify unstageable wounds",
          "Recognize deep tissue injury",
          "Document staging accurately"
        ],
        required: true,
        discipline: "RN"
      },
      {
        id: "wound-3",
        title: "Wound Prevention Protocols",
        description: "Turning schedules, pressure redistribution, nutrition, skin assessment",
        duration: "30m",
        level: "All Disciplines",
        youtubeId: "W9Y9A1-b1bU",
        instructor: "Clinical Education",
        ceuCredits: 0.5,
        objectives: [
          "Implement turning schedules",
          "Use pressure redistribution devices",
          "Educate families on prevention",
          "Complete skin risk assessments"
        ],
        required: true
      },
      {
        id: "wound-4",
        title: "Palliative Wound Management",
        description: "Managing malignant wounds, odor control, drainage management",
        duration: "45m",
        level: "RN",
        youtubeId: "7DZeXL-6TOc",
        instructor: "Wound Care Specialist",
        ceuCredits: 1.0,
        objectives: [
          "Manage malignant wounds",
          "Control wound odor",
          "Handle excessive drainage",
          "Prioritize patient comfort"
        ],
        required: false,
        discipline: "RN"
      }
    ]
  },
  // Hospice Aide Track
  aide_track: {
    title: "Hospice Aide Training",
    description: "Personal care, comfort measures, and end-of-life support for aides/CNAs",
    icon: Home,
    color: "bg-teal-600",
    totalCEUs: 4.0,
    discipline: "Aide",
    courses: [
      {
        id: "aide-1",
        title: "Hospice Aide Role & Responsibilities",
        description: "Overview of aide duties, scope of practice, reporting requirements",
        duration: "30m",
        level: "Aide/CNA",
        youtubeId: "HJ1KpTJ2tCc",
        instructor: "Aide Supervisor",
        ceuCredits: 0.5,
        objectives: [
          "Understand aide scope of practice",
          "Know reporting requirements",
          "Document care provided",
          "Communicate changes to RN"
        ],
        required: true,
        discipline: "Aide"
      },
      {
        id: "aide-2",
        title: "Personal Care & Comfort",
        description: "Bathing, grooming, oral care, positioning, and dignity in care",
        duration: "45m",
        level: "Aide/CNA",
        youtubeId: "RtZWil9lGrs",
        instructor: "Clinical Educator",
        ceuCredits: 1.0,
        objectives: [
          "Provide gentle personal care",
          "Maintain patient dignity",
          "Position for comfort",
          "Perform oral care safely"
        ],
        required: true,
        discipline: "Aide"
      },
      {
        id: "aide-3",
        title: "Signs & Symptoms to Report",
        description: "Recognizing changes, pain indicators, skin changes, when to call RN",
        duration: "30m",
        level: "Aide/CNA",
        youtubeId: "XxXcG0Y7-r4",
        instructor: "Clinical Educator",
        ceuCredits: 0.5,
        objectives: [
          "Recognize reportable changes",
          "Identify pain indicators",
          "Observe skin changes",
          "Report to RN promptly"
        ],
        required: true,
        discipline: "Aide"
      },
      {
        id: "aide-4",
        title: "End-of-Life Comfort Care",
        description: "Care during active dying, family support, post-mortem care basics",
        duration: "45m",
        level: "Aide/CNA",
        youtubeId: "1AwjKYqv5y0",
        instructor: "Clinical Educator",
        ceuCredits: 1.0,
        objectives: [
          "Provide comfort during dying",
          "Support family members",
          "Assist with post-mortem care",
          "Practice self-care"
        ],
        required: true,
        discipline: "Aide"
      },
      {
        id: "aide-5",
        title: "Post-Mortem Care",
        description: "Respectful care after death, body preparation, family support",
        duration: "20m",
        level: "Aide/CNA",
        youtubeId: "RT6bPH2czJM",
        instructor: "Clinical Educator",
        ceuCredits: 0.5,
        objectives: [
          "Provide respectful post-mortem care",
          "Prepare body appropriately",
          "Support grieving family",
          "Follow agency protocols"
        ],
        required: false,
        discipline: "Aide"
      },
      {
        id: "aide-6",
        title: "Infection Control & Safety",
        description: "Hand hygiene, PPE, safe transfers, fall prevention",
        duration: "30m",
        level: "Aide/CNA",
        youtubeId: "ZFhTCp-p_JY",
        instructor: "Infection Control",
        ceuCredits: 0.5,
        objectives: [
          "Practice proper hand hygiene",
          "Use PPE correctly",
          "Perform safe transfers",
          "Prevent patient falls"
        ],
        required: true,
        discipline: "Aide"
      }
    ]
  },
  elnec_core: {
    title: "ELNEC Core Curriculum",
    description: "End-of-Life Nursing Education Consortium - Essential palliative care competencies",
    icon: ScrollText,
    color: "bg-indigo-600",
    isELNEC: true,
    totalCEUs: 8.0,
    courses: [
      {
        id: "elnec-1",
        title: "Introduction to Palliative Nursing",
        description: "Palliative care philosophy, nurse roles, holistic symptom management",
        duration: "1h 30m",
        level: "ELNEC Core",
        youtubeId: "82QsMmA5cKs",
        instructor: "ELNEC Faculty",
        ceuCredits: 1.5,
        objectives: [
          "Define palliative care philosophy",
          "Describe nurse roles in IDT",
          "Identify symptoms affecting QOL",
          "Apply holistic assessments"
        ],
        required: true,
        elnecModule: 1
      },
      {
        id: "elnec-2",
        title: "Communication in Palliative Care",
        description: "Active listening, mindful presence, family conferences",
        duration: "1h 15m",
        level: "ELNEC Core",
        youtubeId: "URjkGCvlWKg",
        startTime: 600,
        instructor: "ELNEC Faculty",
        ceuCredits: 1.25,
        objectives: [
          "Practice active listening",
          "Demonstrate mindful presence",
          "Facilitate family conferences",
          "Navigate difficult conversations"
        ],
        required: true,
        elnecModule: 2
      },
      {
        id: "elnec-3",
        title: "Pain Assessment and Management",
        description: "Comprehensive pain assessments, pharmacological and non-pharmacological interventions",
        duration: "1h 30m",
        level: "ELNEC Core",
        youtubeId: "uFuyKotwKTA",
        instructor: "ELNEC Faculty",
        ceuCredits: 1.5,
        objectives: [
          "Conduct pain assessments",
          "Apply pharmacological interventions",
          "Implement non-pharm measures",
          "Evaluate effectiveness"
        ],
        required: true,
        elnecModule: 3,
        cahpsRelated: true
      },
      {
        id: "elnec-4",
        title: "Symptom Management",
        description: "Biopsychosocial-spiritual model for common symptoms",
        duration: "1h 30m",
        level: "ELNEC Core",
        youtubeId: "82QsMmA5cKs",
        startTime: 3000,
        instructor: "ELNEC Faculty",
        ceuCredits: 1.5,
        objectives: [
          "Apply biopsychosocial model",
          "Manage respiratory symptoms",
          "Address GI symptoms",
          "Treat neurological symptoms"
        ],
        required: true,
        elnecModule: 4,
        cahpsRelated: true
      },
      {
        id: "elnec-5",
        title: "Loss, Grief, and Bereavement",
        description: "Grief stages, compassion fatigue, burnout prevention",
        duration: "1h 15m",
        level: "ELNEC Core",
        youtubeId: "URjkGCvlWKg",
        startTime: 2400,
        instructor: "ELNEC Faculty",
        ceuCredits: 1.25,
        objectives: [
          "Understand grief theories",
          "Recognize compassion fatigue",
          "Implement self-care strategies",
          "Support bereaved families"
        ],
        required: true,
        elnecModule: 5
      },
      {
        id: "elnec-6",
        title: "Final Hours of Life",
        description: "Death preparation, end-of-life symptoms, cultural care",
        duration: "1h 30m",
        level: "ELNEC Core",
        youtubeId: "b8Eqw2RBRiQ",
        instructor: "ELNEC Faculty",
        ceuCredits: 1.5,
        objectives: [
          "Recognize signs of imminent death",
          "Manage end-of-life symptoms",
          "Provide cultural/spiritual care",
          "Support family during death"
        ],
        required: true,
        elnecModule: 6
      }
    ]
  },
  fundamentals: {
    title: "Hospice Fundamentals",
    description: "Core concepts and philosophy of hospice care",
    icon: BookOpen,
    color: "bg-blue-500",
    totalCEUs: 3.5,
    courses: [
      {
        id: "fund-1",
        title: "Crash Course: Hospice Care Primer",
        description: "Comprehensive training on EOL care, medications, symptom management",
        duration: "1h 45m",
        level: "Beginner",
        youtubeId: "82QsMmA5cKs",
        instructor: "Hospice Education",
        ceuCredits: 1.75,
        objectives: [
          "Understand hospice philosophy",
          "Learn medication management",
          "Master symptom management",
          "Educate patients/families"
        ],
        required: true
      },
      {
        id: "fund-2",
        title: "Hospice 101",
        description: "Debunks myths, comfort-focused care, pain control, family support",
        duration: "59m",
        level: "Beginner",
        youtubeId: "URjkGCvlWKg",
        instructor: "Hospice Foundation",
        ceuCredits: 1.0,
        objectives: [
          "Debunk hospice myths",
          "Understand symptom control",
          "Learn about ADLs",
          "Support caregivers"
        ],
        required: true
      },
      {
        id: "fund-3",
        title: "Hospice Care Training 2025",
        description: "IDT roles, patient education, end-of-life arrangements",
        duration: "29m",
        level: "Beginner",
        youtubeId: "b8Eqw2RBRiQ",
        instructor: "Healthcare Training",
        ceuCredits: 0.5,
        objectives: [
          "Understand IDT roles",
          "Learn patient education",
          "Master comfort measures",
          "Recognize dying signs"
        ],
        required: false
      }
    ]
  },
  pain_management: {
    title: "Pain & Symptom Management",
    description: "Critical skills for managing patient comfort - CAHPS Focus Area",
    icon: Stethoscope,
    color: "bg-red-500",
    cahpsScore: 70,
    targetScore: 82,
    totalCEUs: 2.5,
    courses: [
      {
        id: "pain-1",
        title: "Pain Assessment & Management Fundamentals",
        description: "Learn validated pain scales, assessment techniques for verbal and non-verbal patients",
        duration: "25m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 2400,
        instructor: "Clinical Education",
        ceuCredits: 0.5,
        objectives: [
          "Use validated pain assessment scales",
          "Assess pain in non-verbal patients",
          "Document pain levels accurately",
          "Understand breakthrough pain"
        ],
        required: true,
        cahpsRelated: true
      },
      {
        id: "pain-2",
        title: "Hospice & Palliative Care - Beyond the Myths",
        description: "Addresses symptom management myths, early intervention strategies",
        duration: "1h",
        level: "Intermediate",
        youtubeId: "uFuyKotwKTA",
        instructor: "Palliative Care Institute",
        ceuCredits: 1.0,
        objectives: [
          "Dispel medication myths",
          "Learn early intervention strategies",
          "Understand the care continuum",
          "Implement comfort measures"
        ],
        required: true,
        cahpsRelated: true
      },
      {
        id: "pain-3",
        title: "Non-Pharmacological Comfort Measures",
        description: "Positioning, heat/cold therapy, relaxation techniques for symptom relief",
        duration: "20m",
        level: "Beginner",
        youtubeId: "URjkGCvlWKg",
        startTime: 1800,
        instructor: "Comfort Care Team",
        ceuCredits: 0.5,
        objectives: [
          "Master positioning techniques",
          "Apply heat/cold therapy safely",
          "Use relaxation and distraction",
          "Create calming environments"
        ],
        required: false,
        cahpsRelated: true
      },
      {
        id: "pain-4",
        title: "Opioid Safety & Administration",
        description: "Safe opioid prescribing, administration, and monitoring in hospice care",
        duration: "30m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 4200,
        instructor: "Pharmacy Education",
        ceuCredits: 0.5,
        objectives: [
          "Understand opioid pharmacology",
          "Calculate equianalgesic doses",
          "Monitor for adverse effects",
          "Educate families on safe use"
        ],
        required: true,
        cahpsRelated: true
      }
    ]
  },
  timely_response: {
    title: "Timely Care & Response",
    description: "Improving response times and communication - CAHPS Focus Area",
    icon: Clock,
    color: "bg-orange-500",
    cahpsScore: 68,
    targetScore: 80,
    totalCEUs: 1.5,
    courses: [
      {
        id: "timely-1",
        title: "Rapid Response Protocol Training",
        description: "Learn the 30-minute callback standard and urgent response workflows",
        duration: "15m",
        level: "Beginner",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 600,
        instructor: "Quality Improvement",
        ceuCredits: 0.25,
        objectives: [
          "Implement 30-minute callback standard",
          "Identify urgent vs routine requests",
          "Document response times",
          "Escalate appropriately"
        ],
        required: true,
        cahpsRelated: true
      },
      {
        id: "timely-2",
        title: "Effective Triage Communication",
        description: "Phone triage skills, active listening, and setting expectations",
        duration: "20m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 3600,
        instructor: "Communication Training",
        ceuCredits: 0.5,
        objectives: [
          "Master phone triage techniques",
          "Practice active listening",
          "Set realistic expectations",
          "Handle urgent situations"
        ],
        required: true,
        cahpsRelated: true
      },
      {
        id: "timely-3",
        title: "On-Call Excellence",
        description: "Best practices for on-call staff to ensure timely patient care",
        duration: "15m",
        level: "Intermediate",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 1200,
        instructor: "Clinical Leadership",
        ceuCredits: 0.25,
        objectives: [
          "Pre-shift preparation checklist",
          "Resource availability check",
          "Handoff communication standards",
          "Emergency protocol execution"
        ],
        required: false,
        cahpsRelated: true
      },
      {
        id: "timely-4",
        title: "Crisis Management & De-escalation",
        description: "Managing family crises and de-escalating tense situations",
        duration: "30m",
        level: "Advanced",
        youtubeId: "URjkGCvlWKg",
        startTime: 1200,
        instructor: "Social Work Team",
        ceuCredits: 0.5,
        objectives: [
          "Recognize crisis warning signs",
          "Apply de-escalation techniques",
          "Coordinate emergency resources",
          "Document crisis interventions"
        ],
        required: false,
        cahpsRelated: true
      }
    ]
  },
  communication: {
    title: "Communication Excellence",
    description: "Building strong relationships with patients and families",
    icon: MessageSquare,
    color: "bg-purple-500",
    cahpsScore: 78,
    targetScore: 85,
    totalCEUs: 2.0,
    courses: [
      {
        id: "comm-1",
        title: "Family Communication Skills",
        description: "Techniques for compassionate, clear communication with families",
        duration: "30m",
        level: "Intermediate",
        youtubeId: "URjkGCvlWKg",
        startTime: 2400,
        instructor: "Social Work Team",
        ceuCredits: 0.5,
        objectives: [
          "Deliver difficult news compassionately",
          "Address family conflicts",
          "Support grieving families",
          "Document conversations"
        ],
        required: true
      },
      {
        id: "comm-2",
        title: "Goals of Care Conversations",
        description: "Leading effective goals of care discussions with patients and families",
        duration: "45m",
        level: "Advanced",
        youtubeId: "CXuJc35-gf0",
        instructor: "Palliative Medicine",
        ceuCredits: 0.75,
        objectives: [
          "Structure GOC conversations",
          "Elicit patient values and preferences",
          "Navigate family disagreements",
          "Document advance directives"
        ],
        required: true
      },
      {
        id: "comm-3",
        title: "Cultural Competency in End-of-Life Care",
        description: "Understanding diverse cultural perspectives on death and dying",
        duration: "45m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 5400,
        instructor: "Diversity & Inclusion",
        ceuCredits: 0.75,
        objectives: [
          "Recognize cultural influences on care",
          "Respect diverse beliefs about death",
          "Adapt communication styles",
          "Avoid cultural assumptions"
        ],
        required: false
      }
    ]
  },
  emotional_support: {
    title: "Emotional & Spiritual Care",
    description: "Providing holistic support for patients and families",
    icon: Heart,
    color: "bg-pink-500",
    cahpsScore: 93,
    targetScore: 95,
    totalCEUs: 1.5,
    courses: [
      {
        id: "emo-1",
        title: "Emotional Support Fundamentals",
        description: "Providing the right amount of emotional support to patients and families",
        duration: "25m",
        level: "Intermediate",
        youtubeId: "URjkGCvlWKg",
        startTime: 1200,
        instructor: "Chaplaincy Services",
        ceuCredits: 0.5,
        objectives: [
          "Recognize emotional needs",
          "Provide appropriate support",
          "Respect spiritual beliefs",
          "Connect with chaplain services"
        ],
        required: true
      },
      {
        id: "emo-2",
        title: "Spiritual Care Assessment",
        description: "Assessing and addressing spiritual needs across faith traditions",
        duration: "30m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 5400,
        instructor: "Chaplaincy Services",
        ceuCredits: 0.5,
        objectives: [
          "Conduct spiritual assessments",
          "Identify spiritual distress",
          "Support diverse faith traditions",
          "Coordinate chaplain referrals"
        ],
        required: false
      },
      {
        id: "emo-3",
        title: "Self-Care for Caregivers",
        description: "Preventing burnout and compassion fatigue in hospice staff",
        duration: "30m",
        level: "All Levels",
        youtubeId: "URjkGCvlWKg",
        startTime: 3000,
        instructor: "Employee Wellness",
        ceuCredits: 0.5,
        objectives: [
          "Recognize burnout signs",
          "Implement self-care strategies",
          "Build resilience",
          "Access support resources"
        ],
        required: true
      }
    ]
  },
  // Social Worker Track
  social_work: {
    title: "Social Work (MSW/BSW)",
    description: "Psychosocial support, grief counseling, and resource navigation for hospice social workers",
    icon: Users,
    color: "bg-purple-600",
    totalCEUs: 6.0,
    discipline: "Social Work",
    courses: [
      {
        id: "sw-1",
        title: "Psychosocial Assessment in Hospice",
        description: "Comprehensive psychosocial assessment techniques, screening tools, and documentation",
        duration: "45m",
        level: "MSW/BSW",
        youtubeId: "URjkGCvlWKg",
        startTime: 1200,
        instructor: "Social Work Education",
        ceuCredits: 1.0,
        objectives: [
          "Conduct comprehensive psychosocial assessments",
          "Use validated screening tools",
          "Document psychosocial needs",
          "Develop person-centered care plans"
        ],
        required: true,
        discipline: "Social Work"
      },
      {
        id: "sw-2",
        title: "Grief & Bereavement Counseling",
        description: "Anticipatory grief, complicated grief, bereavement support techniques",
        duration: "1h",
        level: "MSW/BSW",
        youtubeId: "82QsMmA5cKs",
        startTime: 2400,
        instructor: "Bereavement Coordinator",
        ceuCredits: 1.5,
        objectives: [
          "Understand grief theories and models",
          "Support anticipatory grief",
          "Identify complicated grief",
          "Facilitate bereavement groups"
        ],
        required: true,
        discipline: "Social Work"
      },
      {
        id: "sw-3",
        title: "Family Systems & Dynamics",
        description: "Working with complex family dynamics, conflict resolution, family meetings",
        duration: "45m",
        level: "MSW/BSW",
        youtubeId: "URjkGCvlWKg",
        startTime: 3000,
        instructor: "Social Work Education",
        ceuCredits: 1.0,
        objectives: [
          "Assess family dynamics",
          "Facilitate family meetings",
          "Navigate family conflict",
          "Support decision-making processes"
        ],
        required: true,
        discipline: "Social Work"
      },
      {
        id: "sw-4",
        title: "Community Resources & Care Coordination",
        description: "Resource navigation, benefit assistance, care transitions, community partnerships",
        duration: "30m",
        level: "MSW/BSW",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 1200,
        instructor: "Resource Coordinator",
        ceuCredits: 0.5,
        objectives: [
          "Navigate community resources",
          "Assist with benefit applications",
          "Coordinate care transitions",
          "Build community partnerships"
        ],
        required: true,
        discipline: "Social Work"
      },
      {
        id: "sw-5",
        title: "Ethical Decision-Making in Hospice",
        description: "Ethical frameworks, informed consent, advance directives, surrogate decision-making",
        duration: "45m",
        level: "MSW/BSW",
        youtubeId: "uFuyKotwKTA",
        startTime: 2400,
        instructor: "Ethics Committee",
        ceuCredits: 1.0,
        objectives: [
          "Apply ethical frameworks",
          "Support informed consent",
          "Navigate advance directives",
          "Guide surrogate decision-makers"
        ],
        required: true,
        discipline: "Social Work"
      },
      {
        id: "sw-6",
        title: "Crisis Intervention & Safety Planning",
        description: "Mental health crisis response, safety planning, suicide risk assessment in hospice",
        duration: "45m",
        level: "MSW/BSW",
        youtubeId: "82QsMmA5cKs",
        startTime: 4200,
        instructor: "Crisis Intervention Specialist",
        ceuCredits: 1.0,
        objectives: [
          "Recognize mental health crises",
          "Conduct safety assessments",
          "Develop safety plans",
          "Coordinate crisis response"
        ],
        required: false,
        discipline: "Social Work"
      }
    ]
  },
  // Chaplain Track
  chaplaincy: {
    title: "Chaplaincy & Spiritual Care",
    description: "Spiritual assessment, multi-faith care, presence, and end-of-life rituals",
    icon: Cross,
    color: "bg-violet-600",
    totalCEUs: 5.5,
    discipline: "Chaplain",
    courses: [
      {
        id: "chap-1",
        title: "Spiritual Assessment in Hospice",
        description: "FICA, HOPE, and other spiritual assessment tools, documentation",
        duration: "45m",
        level: "Chaplain/BCC",
        youtubeId: "URjkGCvlWKg",
        startTime: 1800,
        instructor: "Chaplaincy Services",
        ceuCredits: 1.0,
        objectives: [
          "Use FICA spiritual assessment",
          "Apply HOPE spiritual history",
          "Document spiritual needs",
          "Integrate spirituality in care plan"
        ],
        required: true,
        discipline: "Chaplain"
      },
      {
        id: "chap-2",
        title: "Multi-Faith End-of-Life Care",
        description: "Religious and cultural practices, death rituals across traditions",
        duration: "1h",
        level: "Chaplain/BCC",
        youtubeId: "82QsMmA5cKs",
        startTime: 3600,
        instructor: "Interfaith Chaplain",
        ceuCredits: 1.5,
        objectives: [
          "Understand diverse faith traditions",
          "Support end-of-life rituals",
          "Navigate cultural death practices",
          "Provide inclusive spiritual care"
        ],
        required: true,
        discipline: "Chaplain"
      },
      {
        id: "chap-3",
        title: "Ministry of Presence",
        description: "Being present, active listening, therapeutic silence, sacred space",
        duration: "30m",
        level: "Chaplain/BCC",
        youtubeId: "URjkGCvlWKg",
        startTime: 4200,
        instructor: "Senior Chaplain",
        ceuCredits: 0.5,
        objectives: [
          "Practice ministry of presence",
          "Use therapeutic silence",
          "Create sacred space",
          "Embody compassionate presence"
        ],
        required: true,
        discipline: "Chaplain"
      },
      {
        id: "chap-4",
        title: "Spiritual Distress & Existential Suffering",
        description: "Recognizing and addressing spiritual pain, meaning-making, hope",
        duration: "45m",
        level: "Chaplain/BCC",
        youtubeId: "uFuyKotwKTA",
        startTime: 3000,
        instructor: "Chaplaincy Services",
        ceuCredits: 1.0,
        objectives: [
          "Identify spiritual distress",
          "Address existential suffering",
          "Support meaning-making",
          "Foster realistic hope"
        ],
        required: true,
        discipline: "Chaplain"
      },
      {
        id: "chap-5",
        title: "Death Vigil & Memorial Services",
        description: "Leading vigils, memorial services, celebration of life, legacy work",
        duration: "30m",
        level: "Chaplain/BCC",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 600,
        instructor: "Senior Chaplain",
        ceuCredits: 0.5,
        objectives: [
          "Lead death vigils",
          "Plan memorial services",
          "Support legacy work",
          "Facilitate rituals of closure"
        ],
        required: true,
        discipline: "Chaplain"
      },
      {
        id: "chap-6",
        title: "Staff Support & Moral Distress",
        description: "Supporting team members, moral distress, debriefing, staff memorials",
        duration: "45m",
        level: "Chaplain/BCC",
        youtubeId: "82QsMmA5cKs",
        startTime: 5100,
        instructor: "Chaplaincy Services",
        ceuCredits: 1.0,
        objectives: [
          "Support staff wellbeing",
          "Address moral distress",
          "Lead debriefing sessions",
          "Facilitate staff memorials"
        ],
        required: false,
        discipline: "Chaplain"
      }
    ]
  },
  // Time of Death Care Track - Essential for all staff
  time_of_death: {
    title: "Time of Death Care",
    description: "Essential knowledge for supporting families and handling procedures at time of death",
    icon: Heart,
    color: "bg-slate-700",
    totalCEUs: 4.0,
    discipline: "All Staff",
    courses: [
      {
        id: "tod-1",
        title: "Recognizing Active Dying",
        description: "Physical signs of imminent death, timeline expectations, and communicating with families",
        duration: "45m",
        level: "Required",
        youtubeId: "82QsMmA5cKs",
        startTime: 0,
        instructor: "Clinical Education",
        ceuCredits: 0.75,
        objectives: [
          "Identify signs of active dying (mottling, breathing changes, decreased consciousness)",
          "Understand typical timeline of final hours/days",
          "Communicate prognosis compassionately to families",
          "Distinguish between normal dying process and symptoms needing intervention"
        ],
        required: true,
        discipline: "All"
      },
      {
        id: "tod-2",
        title: "Comfort Measures in Final Hours",
        description: "Medication management, positioning, mouth care, and family involvement in comfort",
        duration: "1h",
        level: "Required",
        youtubeId: "uFuyKotwKTA",
        startTime: 600,
        instructor: "Palliative Care Specialist",
        ceuCredits: 1.0,
        objectives: [
          "Manage terminal secretions (death rattle) with positioning and medications",
          "Provide effective mouth care and skin care",
          "Adjust pain medications appropriately",
          "Guide families in providing comfort measures",
          "Manage terminal agitation/restlessness"
        ],
        required: true,
        discipline: "All",
        cahpsRelated: true
      },
      {
        id: "tod-3",
        title: "What To Do When Death Occurs",
        description: "Step-by-step procedures, legal requirements, and immediate family support",
        duration: "45m",
        level: "Required",
        youtubeId: "URjkGCvlWKg",
        startTime: 0,
        instructor: "Clinical Director",
        ceuCredits: 0.75,
        objectives: [
          "Confirm death (absence of pulse, breathing, response)",
          "Contact appropriate personnel (RN on-call, physician, coroner if needed)",
          "Know when coroner notification is required",
          "Document time of death and circumstances",
          "Handle personal belongings and medications appropriately"
        ],
        required: true,
        discipline: "All"
      },
      {
        id: "tod-4",
        title: "Supporting the Family at Death",
        description: "Presence, communication, cultural considerations, and immediate grief support",
        duration: "45m",
        level: "Required",
        youtubeId: "CXuJc35-gf0",
        startTime: 1200,
        instructor: "Bereavement Coordinator",
        ceuCredits: 0.75,
        objectives: [
          "Provide ministry of presence during and after death",
          "Allow family time with deceased (no rush)",
          "Respect cultural and religious practices",
          "Offer immediate grief support and next steps",
          "Answer common family questions compassionately"
        ],
        required: true,
        discipline: "All"
      },
      {
        id: "tod-5",
        title: "Post-Mortem Care & Body Preparation",
        description: "Bathing, dressing, positioning, and preparing for funeral home transfer",
        duration: "30m",
        level: "Required",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 1800,
        instructor: "Clinical Education",
        ceuCredits: 0.5,
        objectives: [
          "Perform post-mortem bathing with dignity",
          "Position body appropriately",
          "Remove or secure medical devices as protocol",
          "Dress or wrap body per family wishes",
          "Prepare for funeral home transfer"
        ],
        required: true,
        discipline: "All"
      },
      {
        id: "tod-6",
        title: "Self-Care After Patient Death",
        description: "Processing grief, avoiding burnout, and healthy coping strategies for caregivers",
        duration: "30m",
        level: "Recommended",
        youtubeId: "82QsMmA5cKs",
        startTime: 3600,
        instructor: "Wellness Coordinator",
        ceuCredits: 0.25,
        objectives: [
          "Recognize signs of compassion fatigue",
          "Practice healthy grief processing",
          "Use available support resources",
          "Maintain work-life boundaries",
          "Build resilience in hospice work"
        ],
        required: false,
        discipline: "All"
      }
    ],
    quickReference: {
      title: "Quick Reference: When Death Occurs",
      steps: [
        "1. STAY CALM - Your presence is comforting to the family",
        "2. CONFIRM DEATH - No pulse, no breathing, no response to stimuli",
        "3. NOTE THE TIME - Document the time you confirmed death",
        "4. CALL THE HOSPICE - Contact the 24/7 on-call nurse line",
        "5. DO NOT CALL 911 - Unless unexpected/traumatic death",
        "6. STAY WITH FAMILY - Offer presence, no need to rush",
        "7. WAIT FOR GUIDANCE - The hospice team will guide next steps"
      ],
      doNotDo: [
        "DO NOT call 911 (unless instructed by hospice)",
        "DO NOT rush the family or remove the body quickly",
        "DO NOT dispose of medications without guidance",
        "DO NOT express shock or distress in front of family"
      ],
      familyQuestions: [
        "\"What happens now?\" - The hospice nurse will come, then funeral home will be called",
        "\"How long can they stay?\" - As long as you need, there's no rush",
        "\"What about medications?\" - The hospice will handle medication disposal",
        "\"When do we call the funeral home?\" - After the hospice nurse arrives and completes paperwork"
      ]
    }
  },
  compliance: {
    title: "Compliance & Documentation",
    description: "Meeting regulatory requirements and best practices",
    icon: Shield,
    color: "bg-green-500",
    totalCEUs: 2.0,
    courses: [
      {
        id: "comp-1",
        title: "HIPAA & Patient Privacy",
        description: "Understanding and maintaining patient privacy in hospice care",
        duration: "30m",
        level: "Required",
        youtubeId: "b8Eqw2RBRiQ",
        startTime: 0,
        instructor: "Compliance Officer",
        ceuCredits: 0.5,
        objectives: [
          "Understand HIPAA requirements",
          "Protect patient information",
          "Handle breaches appropriately",
          "Document securely"
        ],
        required: true
      },
      {
        id: "comp-2",
        title: "Clinical Documentation Best Practices",
        description: "Accurate, timely documentation for quality care and compliance",
        duration: "45m",
        level: "Intermediate",
        youtubeId: "82QsMmA5cKs",
        startTime: 4800,
        instructor: "Quality Assurance",
        ceuCredits: 0.75,
        objectives: [
          "Document visits thoroughly",
          "Use proper terminology",
          "Meet timing requirements",
          "Support care coordination"
        ],
        required: true
      },
      {
        id: "comp-3",
        title: "Medicare Conditions of Participation",
        description: "Understanding Medicare CoPs for hospice certification",
        duration: "45m",
        level: "Advanced",
        youtubeId: "CXuJc35-gf0",
        startTime: 1800,
        instructor: "Regulatory Affairs",
        ceuCredits: 0.75,
        objectives: [
          "Understand CoP requirements",
          "Maintain survey readiness",
          "Document for compliance",
          "Address deficiencies"
        ],
        required: false
      }
    ]
  }
};


export const TRAINING_COURSES = ELNEC_MODULES;

export const ACHIEVEMENTS = [
  { id: 'first_course', title: 'First Steps', description: 'Complete your first course', icon: 'Star', threshold: 1 },
  { id: 'elnec_complete', title: 'ELNEC Certified', description: 'Complete all ELNEC Core modules', icon: 'ScrollText', threshold: 6 },
  { id: 'pain_expert', title: 'Comfort Care Champion', description: 'Complete all pain management courses', icon: 'Stethoscope', threshold: 4 },
  { id: 'timely_master', title: 'Response Ready', description: 'Complete all timely care courses', icon: 'Clock', threshold: 4 },
  { id: 'communicator', title: 'Communication Pro', description: 'Complete all communication courses', icon: 'MessageSquare', threshold: 3 },
  { id: 'fundamentals', title: 'Foundation Builder', description: 'Complete all fundamentals courses', icon: 'BookOpen', threshold: 3 },
  { id: 'rncm_certified', title: 'RNCM Certified', description: 'Complete all RN Case Manager courses', icon: 'UserCog', threshold: 11 },
  { id: 'wound_specialist', title: 'Wound Care Specialist', description: 'Complete all wound care courses', icon: 'Bandage', threshold: 4 },
  { id: 'aide_certified', title: 'Aide Excellence', description: 'Complete all hospice aide courses', icon: 'Home', threshold: 6 },
  { id: 'sw_certified', title: 'Social Work Expert', description: 'Complete all social work courses', icon: 'Users', threshold: 6 },
  { id: 'chaplain_certified', title: 'Spiritual Care Champion', description: 'Complete all chaplaincy courses', icon: 'Cross', threshold: 6 },
  { id: 'tod_certified', title: 'Time of Death Certified', description: 'Complete all Time of Death care courses', icon: 'Heart', threshold: 6 },
  { id: 'ceu_milestone', title: 'CEU Champion', description: 'Earn 10+ CEU credits', icon: 'Award', threshold: 10 },
  { id: 'ceu_master', title: 'CEU Master', description: 'Earn 25+ CEU credits', icon: 'Trophy', threshold: 25 },
  { id: 'all_required', title: 'Core Complete', description: 'Complete all required courses', icon: 'BadgeCheck', threshold: 15 },
  { id: 'academy_graduate', title: 'Academy Graduate', description: 'Complete all courses', icon: 'GraduationCap', threshold: 25 }
];
